import {
  createRouter,
  createWebHistory,
  type Router,
  type RouteLocationNormalized,
  type NavigationGuardNext,
  RouteRecordRaw,
} from 'vue-router';
import { h } from 'vue';
import {
  useTourOperatorsStore,
  useRootStore,
} from '@/stores/index.js';
import { APP_IDS, type AppId, useCurrentAppStore } from '@/stores/current-app-store.ts';

import { AllotmentRolesConstants as Roles, TourOperatorRolesConstants as TourOperatorRoles } from '@/stores/definitions.js';
import adminRoutes from './routes/admin.ts';
import charterRoutes from './routes/charter.ts';
import financeRoutes from './routes/finance.ts';
import AppLoadingLayout from "@/layouts/AppLoadingLayout.vue";
import {route} from "@/router/router-helpers.ts";
import {useCurrentUserStore} from "@/stores/current-user-store.ts";
import {isFeatureEnabled} from "@/config/feature-flags.ts";

// Lazy-loaded components
const Allotments = () => import('@/views/Allotments/index.vue');
const AllotmentsOverview = () => import('@/views/Allotments/Overview/AllotmentsOverview.vue');
const AllotmentsEvolution = () => import('@/views/Allotments/Evolution.vue');
const AllotmentsChangeApprovals = () => import('@/views/Allotments/ChangeApprovals.vue');
const AllotmentsFinancialOverview = () => import('@/views/Allotments/FinancialOverview.vue');
const Reports = () => import('@/views/Reports/Reports.vue');
const DashboardFilesView = () => import('@/views/Dashboard/Files.vue');
const DashboardReportsView = () => import('@/views/Dashboard/Reports.vue');
const PassengerDataView = () => import('@/views/PassengerData/PassengerData.vue');
const ScheduleChangesOverview = () => import('@/views/ScheduleChanges/Overview.vue');
const SearchFlightsView = () => import('@/views/FlightSearch/FlightSearch.vue');
const PurchaseOverviewView = () => import('@/views/FlightSearch/PurchaseOverview.vue');
const Page401View = () => import('@/views/401.vue');
const Page403View = () => import('@/views/403.vue');
const Page404View = () => import('@/views/404.vue');

const titleSuffix = 'Allotment Management Suite';
const ALLOTMENT_APP_ID = 'allotment' as const;
const CHARTER_APP_ID = 'charter' as const;

function isAppId(value: unknown): value is AppId {
  return typeof value === 'string' && APP_IDS.includes(value as AppId);
}

function getHomeRouteName(appId: AppId, canAccessCharter: boolean = true): string {
  if (appId === CHARTER_APP_ID && !canAccessCharter) {
    return 'home-redirect';
  }

  return appId === CHARTER_APP_ID ? 'charter-flights-overview' : 'home-redirect';
}

// Check if pooling is enabled for tour operator
async function checkIfPoolingEnabled(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const currentUserStore = useCurrentUserStore();

  if (currentUserStore.isAdmin || currentUserStore.isReadonlyAdmin) {
    return next();
  }

  if (!currentUserStore.isTourOperatorAllAccess) {
    return next('/403');
  }

  const tourOperatorsStore = useTourOperatorsStore();
  const current = await tourOperatorsStore.getCurrent();

  if (current.isPoolingEnabled) {
    return next();
  }

  return next('/403');
}

export default function createAppRouter(): Router {
  const routes: RouteRecordRaw[] = [
    {
      path: '/',
      name: 'home',
      redirect: () => ({ name: 'home-redirect' }),
    },
    route('loading', AppLoadingLayout),
    {
      path: '/home-redirect',
      name: 'home-redirect',
      component: {
        render: () => h('div'),
      },
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.FINANCE, Roles.TOUR_OPERATOR],
        roleRedirect: {
          [Roles.ADMIN]: 'allotments-overview',
          [Roles.READONLY_ADMIN]: 'allotments-overview',
          [Roles.TOUR_OPERATOR]: 'allotments-overview',
          [Roles.HCC_READONLY]: 'allotments-overview',
          [Roles.FINANCE]: 'finance',
        },
      },
    },
    {
      path: '/auth/signinwin/main',
      component: {
        render: () => h('div'),
        created() {
          router.replace({ name: 'home-redirect' })
        }
      },
      meta: {
        authRequired: false,
      }
    },
    {
      path: '/auth/signinsilent/main',
      redirect: () => ({ name: 'home-redirect' }),
      meta: {
        authRequired: false,
      }
    },
    ...adminRoutes,
    ...charterRoutes,
    ...financeRoutes,
    {
      path: '/allotments',
      name: 'allotments',
      component: Allotments,
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
        title: 'Allotments',
        referenceDataRequired: true,
      },
      children: [
        {
          path: '',
          name: 'admin-landing',
          redirect: () => ({ name: 'allotments-overview' }),
          meta: {
            app: ALLOTMENT_APP_ID,
            authRequired: true,
            allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
          },
        },
        {
          path: 'overview',
          name: 'allotments-overview',
          component: AllotmentsOverview,
          meta: {
            app: ALLOTMENT_APP_ID,
            authRequired: true,
            allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR, Roles.HCC_READONLY],
          },
        },
        {
          path: 'financial-overview',
          name: 'allotments-financial-overview',
          component: AllotmentsFinancialOverview,
          meta: {
            app: ALLOTMENT_APP_ID,
            authRequired: true,
            allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN],
          },
        },
        {
          path: 'change-approvals',
          name: 'change-approvals',
          component: AllotmentsChangeApprovals,
          meta: {
            app: ALLOTMENT_APP_ID,
            authRequired: true,
            allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN],
          },
        },
        {
          path: 'evolution',
          name: 'allotments-evolution',
          component: AllotmentsEvolution,
          meta: {
            app: ALLOTMENT_APP_ID,
            authRequired: true,
            allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
            referenceDataRequired: true,
          },
        },
      ],
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      meta: {
        authRequired: true,
        allowedRoles: [Roles.ADMIN],
        featureFlag: 'dashboardApp',
        title: 'Dashboard',
        referenceDataRequired: true,
      },
      children: [
        {
          path: 'files',
          name: 'dashboard-files',
          component: DashboardFilesView,
          meta: {
            authRequired: true,
            allowedRoles: [Roles.ADMIN],
          },
        },
        {
          path: 'reports',
          name: 'dashboard-reports',
          component: DashboardReportsView,
          meta: {
            authRequired: true,
            allowedRoles: [Roles.ADMIN],
          },
        },
      ],
    },
    {
      path: '/reports/:id?',
      name: 'reports',
      component: Reports,
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
        title: 'Report History',
        referenceDataRequired: true,
      },
    },
    {
      path: '/passenger-data',
      name: 'passengerData',
      component: PassengerDataView,
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
        title: 'Passenger Data',
      },
    },
    {
      path: '/schedule-changes',
      name: 'scheduleChanges',
      component: ScheduleChangesOverview,
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.TOUR_OPERATOR],
        allowedTourOperatorRoles: [TourOperatorRoles.ONLY_REPORTING, TourOperatorRoles.ALL_ACCESS],
        title: 'Schedule changes'
      }
    },
    {
      path: '/search-flights',
      name: 'search-flights',
      component: SearchFlightsView,
      beforeEnter: checkIfPoolingEnabled,
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
        title: 'Search flights',
        referenceDataRequired: false,
      },
    },
    {
      path: '/purchase-overview',
      name: 'purchase-overview',
      component: PurchaseOverviewView,
      beforeEnter: checkIfPoolingEnabled,
      meta: {
        app: ALLOTMENT_APP_ID,
        authRequired: true,
        allowedRoles: [Roles.ADMIN, Roles.READONLY_ADMIN, Roles.TOUR_OPERATOR],
        title: 'Purchase Overview',
        referenceDataRequired: false,
      },
    },
    {
      path: '/401',
      name: '401',
      component: Page401View,
      meta: {
        title: 'Access denied',
      },
    },
    {
      path: '/403',
      name: '403',
      component: Page403View,
      meta: {
        title: 'Forbidden',
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: Page404View,
      meta: {
        title: 'File not found',
      },
    },
  ];

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior() {
      return { top: 0 };
    },
  });

  router.beforeEach(async (to, _from, next) => {
    const currentUserStore = useCurrentUserStore();
    const requiresAuth = to.matched.some(record => record.meta.authRequired);

    // Allow public routes through; for protected routes, let the loading view resolve the user once.
    if (requiresAuth && !currentUserStore.currentUser && to.name !== 'loading') {
      next({ name: 'loading', query: { redirect: to.fullPath } });
      return;
    }

    next();
  })

  // App isolation guard: keep navigation inside the currently selected app.
  router.beforeEach(async (to, _from, next) => {
    const currentAppStore = useCurrentAppStore();
    const userStore = useCurrentUserStore();
    const routeApp = to.meta?.app;
    const requiresAuth = to.matched.some(record => record.meta.authRequired);

    const isInCharterWithoutAccess = currentAppStore.currentApp === CHARTER_APP_ID && !userStore.hasCharterAccess;

    if (requiresAuth && userStore.currentUser && isInCharterWithoutAccess) {
      currentAppStore.setCurrentApp(ALLOTMENT_APP_ID);
    }

    if (isAppId(routeApp) && routeApp !== currentAppStore.currentApp) {
      return next({ name: getHomeRouteName(currentAppStore.currentApp, userStore.hasCharterAccess) });
    }

    return next();
  });

  // Authentication and authorization guard
  router.beforeEach(async (to, _from, next) => {
    const title = to.meta?.title;
    document.title = title ? `${title} - ${titleSuffix}` : titleSuffix;

    const requiresAuth = to.matched.some(record => record.meta.authRequired);
    const hasDisabledFeature = to.matched.some(record => {
      return record.meta.featureFlag !== undefined && !isFeatureEnabled(record.meta.featureFlag);
    });

    if (hasDisabledFeature) {
      return next('404');
    }

    if (requiresAuth) {
      const store = useCurrentUserStore();
      const currentAppStore = useCurrentAppStore();
      const currentUser = store.currentUser;

      if (!currentUser) {
        next('401');
        return;
      }

      if (to.meta.allowedRoles) {
        if (to.meta.roleRedirect) {
          const redirectTarget = to.meta.roleRedirect[Number(currentUser.allotmentRoleId)];

          if (redirectTarget) {
            return next({ name: redirectTarget });
          }

          if (store.hasCharterAccess) {
            currentAppStore.setCurrentApp(CHARTER_APP_ID);
            return next({ name: 'charter-flights-overview' });
          }
          return next('403');
        }
        if (to.meta.allowedRoles.includes(Number(currentUser.allotmentRoleId))) {
          return next();
        }
        return next('403');
      }

      if (to.meta.charterAccessRequired) {
        if (store.hasCharterAccess) {
          return next();
        }
        return next('403');
      }

      if (to.meta.allowedTourOperatorRoles) {
        if (to.meta.allowedTourOperatorRoles.includes(Number(currentUser.tourOperatorRoleId))) {
          return next();
        }
        return next('403');
      }

      next('401');
      return;
    } else {
      next();
      return;
    }
  });

  // Reference data loading guard
  router.beforeEach(async (to, _from, next) => {
    const rootStore = useRootStore();
    const currentAppStore = useCurrentAppStore();

    if (to.meta.referenceDataRequired) {
      try {
        if (currentAppStore.currentApp === CHARTER_APP_ID) {
          await rootStore.loadCharterReferenceData();
        } else {
          await rootStore.loadAllotmentReferenceData();
        }
      } catch (e) {
        console.error(e);
        return next(false);
      }
    }

    next();
  });

  return router;
}
